import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarPlus, MoonStar } from "lucide-react";
import { toast } from "sonner";
import { CEREMONY_DATE, VENUE_NAME } from "../config";
import { useLang } from "../store/lang";

const pad = (n) => String(n).padStart(2, "0");

export const Countdown = () => {
  const [now, setNow] = useState(Date.now());
  const { ur } = useLang();

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const diff = Math.max(0, CEREMONY_DATE.getTime() - now);
  const units = [
    { id: "countdown-days", label: ur ? "دن" : "Days", value: Math.floor(diff / 86400000) },
    { id: "countdown-hours", label: ur ? "گھنٹے" : "Hours", value: Math.floor(diff / 3600000) % 24 },
    { id: "countdown-minutes", label: ur ? "منٹ" : "Minutes", value: Math.floor(diff / 60000) % 60 },
    { id: "countdown-seconds", label: ur ? "سیکنڈ" : "Seconds", value: Math.floor(diff / 1000) % 60 },
  ];

  const addToCalendar = () => {
    const ics = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      "UID:haya-noor-jamal-naming@fairytale",
      "DTSTAMP:20260701T000000Z",
      "DTSTART:20261019T190000",
      "DTEND:20261019T220000",
      "SUMMARY:Haya Noor Jamal — Aqiqah Ceremony",
      `LOCATION:${VENUE_NAME}`,
      "DESCRIPTION:Aqiqah ceremony of Haya Noor Jamal. Dress code: Royal Stardust & Fairytale Formal.",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "haya-aqiqah-ceremony.ics";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(ur ? "تقریب محفوظ ہو گئی — ستاروں تلے ملاقات ہوگی" : "Ceremony saved — see you under the stars");
  };

  return (
    <section id="countdown" className="relative py-28 sm:py-36 px-6" data-testid="countdown-section">
      <div className="max-w-4xl mx-auto text-center">
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="eyebrow mb-4"
        >
          {ur ? "آسمانی گھڑی چل رہی ہے" : "The celestial clock is ticking"}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className={`font-display text-3xl sm:text-5xl text-amber-100 font-light ${ur ? "font-urdu" : ""}`}
          dir={ur ? "rtl" : "ltr"}
        >
          {ur ? "جب تک ستارے اس کا نام لکھیں گے" : "Until the stars spell her name"}
        </motion.h2>

        <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
          {units.map((u, i) => (
            <motion.div
              key={u.id}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="glass-card rounded-2xl px-4 py-8"
            >
              <div data-testid={u.id} className="font-royal text-4xl sm:text-5xl gold-text tabular-nums">
                {pad(u.value)}
              </div>
              <div className="mt-3 text-[0.65rem] tracking-[0.3em] uppercase text-slate-400">{u.label}</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-5"
        >
          <span className="flex items-center gap-2 text-sm text-rose-200/80" dir={ur ? "rtl" : "ltr"}>
            <MoonStar size={16} className="text-amber-300" />
            {ur ? "فصل کے چاند والی خزاں کی شام" : "A harvest-moon autumn evening"}
          </span>
          <button data-testid="add-to-calendar-btn" onClick={addToCalendar} className="btn-ghost">
            <CalendarPlus size={15} /> {ur ? "کیلنڈر میں شامل کریں" : "Add to Calendar"}
          </button>
        </motion.div>
      </div>
    </section>
  );
};
